import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {useAccount, usePublicClient, useSignTypedData} from 'wagmi';
import { DepositWithdrawalService } from '../services/deposit-withdrawal/deposit-withdrawal.service';
import { PositionsManager } from '../services/position/position.manager';
import { ContractContext } from './ContractsProvider';
import { ModalContext } from './ModalProvider';
import { ToasterContext } from './ToasterProvider/ToasterProvider';
import {DepositWithdrawalStakeService} from "../services/deposit-withdrawal-stake/deposit-withdrawal-stake.service";

interface ServicesProviderProps {
    children: ReactNode;
}

export const ServicesContext = createContext({
    depositWithdrawService: new DepositWithdrawalService(),
    depositWithdrawStakeService: new DepositWithdrawalStakeService(),
    positionsManager: new PositionsManager()
});

export const ServicesProvider: React.FC<ServicesProviderProps> = ({ children }) => {
    const publicClient = usePublicClient();
    const { usdtContract, ddpContract, nrsContract, veNrsContract, nrsWithdrawalTimelockContract } = useContext(ContractContext);
    const { addToasterWithStatusAndDescription, removeToaster } = useContext(ToasterContext);
    const { closeModal } = useContext(ModalContext);
    const { address } = useAccount();
    const depositServiceRef = useRef<DepositWithdrawalService>(new DepositWithdrawalService());
    const depositStakeServiceRef = useRef<DepositWithdrawalStakeService>(new DepositWithdrawalStakeService());
    const positionsManagerRef = useRef<PositionsManager>(new PositionsManager());
   const { signTypedDataAsync } = useSignTypedData();

    const [services, setServices] = useState({
        depositWithdrawService: depositServiceRef.current,
        depositWithdrawStakeService: depositStakeServiceRef.current,
        positionsManager: positionsManagerRef.current
    });

    useEffect(() => {

        if (address) {
            positionsManagerRef.current.injectDependencies({
                userAddress: address,
                addToasterWithStatusAndDescription,
                closeModal,
                signTypedData: signTypedDataAsync
            } as any);

            setServices({
                depositWithdrawService: depositServiceRef.current,
                depositWithdrawStakeService: depositStakeServiceRef.current,
                positionsManager: positionsManagerRef.current
            });
        }
    }, [address]);

    useEffect(() => {
        if (usdtContract && address && ddpContract && nrsContract && veNrsContract && nrsWithdrawalTimelockContract) {
            depositServiceRef.current.injectDependencies({
                usdtContract,
                ddpContract,
                userAddress: address,
                addToasterWithStatusAndDescription,
                removeToaster,
                closeModal,
                signTypedData: signTypedDataAsync
            } as any);
            depositStakeServiceRef.current.injectDependencies({
                publicClient,
                nrsContract,
                veNrsContract,
                nrsWithdrawalTimelockContract,
                userAddress: address,
                addToasterWithStatusAndDescription,
                removeToaster,
                closeModal,
                signTypedData: signTypedDataAsync
            } as any);
            setServices({
                depositWithdrawService: depositServiceRef.current,
                depositWithdrawStakeService: depositStakeServiceRef.current,
                positionsManager: positionsManagerRef.current
            });
        }

    }, [nrsContract, veNrsContract, nrsWithdrawalTimelockContract, usdtContract, address, ddpContract, closeModal, addToasterWithStatusAndDescription]);

    return (
        <ServicesContext.Provider value={services}>
            {children}
        </ServicesContext.Provider>
    );
};

// Custom hook to use the services
export const useServices = () => {
    return useContext(ServicesContext);
};
